import { InputBase } from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, LockKeyhole, Mail } from "lucide-react";
import "./login.css";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isStandalonePage = location.pathname === "/login";

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const login = async () => {
    if (!formData.email || !formData.password) {
      setError("Please enter your email and password");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/doctors/login",
        formData
      );

      localStorage.setItem("doctor", JSON.stringify(res.data.doctor));
      localStorage.setItem("token", res.data.token);

      navigate("/dashboard/home");
    } catch (err) {
      setError(err.response?.data?.error || "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    fontSize: "14px",
    fontWeight: 600,
    color: "#0f172a",
    "& input::placeholder": {
      color: "#94a3b8",
      opacity: 1,
    },
  };

  const form = (
    <div className="doctor-login-form">
      {/* HEADER */}
      <div className="doctor-login-header">
        <h2>
          Sign in
        </h2>

        <p>
          Continue to your Zaviramed doctor dashboard.
        </p>
      </div>

      {/* FORM */}
      <div className="doctor-login-fields">
        {/* EMAIL */}
        <div>
          <label className="doctor-login-label">
            <Mail size={14} />
            Email address
          </label>

          <div className="doctor-login-control">
            <InputBase
              placeholder="doctor@example.com"
              type="email"
              fullWidth
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              sx={inputSx}
            />
          </div>
        </div>

        {/* PASSWORD */}
        <div>
          <label className="doctor-login-label">
            <LockKeyhole size={14} />
            Password
          </label>

          <div className="doctor-login-control">
            <InputBase
              placeholder="Enter your password"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
              sx={inputSx}
              onKeyDown={(e) => {
                if (e.key === "Enter") login();
              }}
            />

            <button
              onClick={() => setShowPassword((prev) => !prev)}
              className="doctor-login-icon-button"
              type="button"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* ERROR */}
      {error && (
        <div className="doctor-login-error">
          {error}
        </div>
      )}

      {/* LOGIN BUTTON */}
      <button
        type="button"
        onClick={login}
        disabled={loading}
        className="doctor-login-submit"
      >
        {loading ? "Signing in..." : "Sign in"}
      </button>

      {/* EXTRA */}
      <div className="doctor-login-extra">
        Don’t have an account?{" "}
        <button
          type="button"
          onClick={() => navigate("/register-as-doctor")}
        >
          Join as a doctor
        </button>
      </div>
    </div>
  );

  if (!isStandalonePage) {
    return <div className="w-full">{form}</div>;
  }

  return (
    <main className="doctor-login-screen">
      <div className="doctor-login-pattern" aria-hidden="true" />

      <section className="doctor-login-card" aria-label="Doctor sign in">
        <button
          type="button"
          className="doctor-login-brand"
          onClick={() => navigate("/")}
          aria-label="Go to Zaviramed home"
        >
          <img src="/zavira_nobg.png" alt="Zaviramed" />
          <span>
            <strong>
              Zavira<span>Med</span>
            </strong>
            <small>Doctor dashboard</small>
          </span>
        </button>

        {form}
      </section>
    </main>
  );
};

export default Login;
